import { useState, useEffect, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabaseClient';
import type { ProfileWithDetails } from '@/types/database';

interface UseUserReturn {
  session: Session | null;
  user: User | null;
  profile: ProfileWithDetails | null;
  isLoading: boolean;
  firstName: string;
  avatarUrl: string | null;
}

const profileCache: { [userId: string]: ProfileWithDetails } = {};

export function useUser(): UseUserReturn {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<ProfileWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
        setIsLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      // Check cache first
      if (profileCache[userId]) {
        setProfile(profileCache[userId]);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('*, ai_profile_description, ai_profile_updated_at')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        if (error.code !== 'PGRST116') { // PGRST116 = no rows found
          console.error('Error fetching profile:', error);
        }
        setProfile(null);
      } else {
        if (data) {
          // Cache the profile
          profileCache[userId] = data;
          setProfile(data);
        } else {
          setProfile(null);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile(null);
    } finally {
      setIsLoading(false);
    }
  };

  const derivedData = useMemo(() => {
    let firstName = '';
    let avatarUrl: string | null = null;

    if (profile?.first_name) {
      firstName = profile.first_name;
    } else if (user?.email) {
      firstName = user.email.split('@')[0];
    }

    if (profile?.avatar_url) {
      avatarUrl = profile.avatar_url;
    }

    return { firstName, avatarUrl };
  }, [profile, user]);

  return {
    session,
    user,
    profile,
    isLoading,
    firstName: derivedData.firstName,
    avatarUrl: derivedData.avatarUrl,
  };
}